import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE } from '../constants/api';
import { fetchBlogPost, fetchBlogPosts } from './blog';
import { apiMutation, apiRequest } from './client';
import type {
  BillData,
  HelioQuoteSubmitInput,
  HeroEstimate,
  AddressLookupResponse,
  InstallerProfile,
  PlatformStats,
  PortalUser,
  Project,
  ProjectMessage,
  PropertyMapResult,
  RecommendQuoteInput,
  RecommendResult,
  Referral,
  RemoteSurveyPayload,
  InstallerSurveyListItem,
  InstallerPackPayload,
  SolarBuildingInsightsResult,
  UserNotification,
} from './types';

export interface InstallerAvailability {
  dates: string[];
}

export interface QuoteInstallDates {
  dates: string[];
  earliestSelectable: string;
  selected?: string | null;
}

function normalizePropertyMapResponse(raw: Record<string, unknown>): PropertyMapResult {
  const satellite = (raw.satellite_image ?? raw.satelliteImage) as Record<string, unknown> | undefined;
  const coords = (raw.coordinates) as Record<string, unknown> | undefined;
  const insights = (raw.solar_insights ?? raw.solarInsights) as Record<string, unknown> | undefined;

  return {
    formattedAddress: (raw.formatted_address ?? raw.formattedAddress) as string | undefined,
    coordinates: coords
      ? {
          latitude: Number(coords.latitude),
          longitude: Number(coords.longitude),
        }
      : undefined,
    satelliteImage: satellite?.url
      ? {
          url: String(satellite.url),
          zoomLevel: satellite.zoom_level != null ? Number(satellite.zoom_level) : undefined,
          imageSize: satellite.image_size != null ? String(satellite.image_size) : undefined,
        }
      : undefined,
    solarInsights: insights
      ? {
          available: Boolean(insights.available),
          roofAreaM2: insights.roof_area_m2 != null ? Number(insights.roof_area_m2) : undefined,
          roofOrientation: insights.roof_orientation != null ? String(insights.roof_orientation) : undefined,
          azimuthDegrees: insights.azimuth_degrees != null ? Number(insights.azimuth_degrees) : undefined,
          maxSunshineHoursPerYear:
            insights.max_sunshine_hours_per_year != null
              ? Number(insights.max_sunshine_hours_per_year)
              : undefined,
          shadingFactor: insights.shading_factor != null ? Number(insights.shading_factor) : undefined,
        }
      : undefined,
  };
}

export function useStats() {
  return useQuery({
    queryKey: ['stats'],
    queryFn: () => apiRequest<PlatformStats>('/api/stats'),
    staleTime: 60_000,
  });
}

export function useHeroEstimate(postcode?: string) {
  return useQuery({
    queryKey: ['heroEstimate', postcode ?? ''],
    queryFn: async () => {
      const data = await apiRequest<HeroEstimate>(
        `/api/stats/hero-estimate${postcode ? `?postcode=${encodeURIComponent(postcode)}` : ''}`,
      );
      return { ...data, annualSavings: data.estimatedAnnualSavings };
    },
    staleTime: 60_000,
    enabled: postcode !== undefined,
  });
}

export function useAddressLookup() {
  return useMutation({
    mutationFn: (postcode: string) =>
      apiRequest<AddressLookupResponse>(
        `/api/addresses/lookup?postcode=${encodeURIComponent(postcode.trim().toUpperCase())}`,
      ),
  });
}

export function usePortalUser(enabled = true) {
  return useQuery({
    queryKey: ['portalUser'],
    queryFn: () => apiRequest<PortalUser>('/api/auth/user'),
    retry: false,
    enabled,
  });
}

export function useExtractBill() {
  return useMutation({
    mutationFn: async ({
      fileUrl,
      fileName,
      fileType,
    }: {
      fileUrl: string;
      fileName: string;
      fileType: string;
    }) => {
      const base64 = fileUrl.includes(',') ? fileUrl.split(',')[1] : fileUrl;

      return apiMutation<BillData>('/api/bills/extract-helio', {
        file_data: base64,
        file_type: fileType,
        file_name: fileName,
        use_ai: true,
      });
    },
  });
}

export function useRecommendQuote() {
  return useMutation({
    mutationFn: (input: RecommendQuoteInput) =>
      apiMutation<RecommendResult>('/api/quotes/recommend', input),
  });
}

export function usePropertyMap() {
  return useMutation({
    mutationFn: async (input: { address: string; postcode: string }) => {
      const mapAddress = `${input.address}, ${input.postcode}, UK`;
      const raw = await apiMutation<Record<string, unknown>>('/api/property/map', {
        address: mapAddress,
        zoom: 20,
        size: '600x400',
        include_markers: true,
      });
      return normalizePropertyMapResponse(raw);
    },
  });
}

export function useRoofOutlineReview() {
  return useMutation({
    mutationFn: (input: {
      address: string;
      postcode?: string;
      verdict: 'positive' | 'negative';
      satellite_image_url: string;
      title_geojson?: unknown;
      roof_geojson: unknown;
      segment_centres?: unknown;
    }) => apiMutation<{ id: number; verdict: string }>('/api/property/roof-outline-corrections', input),
  });
}

export function useSolarBuildingInsights() {
  return useMutation({
    mutationFn: async (input: {
      uprn?: string;
      address?: string;
      postcode?: string;
      latitude?: number;
      longitude?: number;
      title_geojson_path?: string;
      buffer_m?: number;
      include_data_layers?: boolean;
      skip_title_lookup?: boolean;
      satellite_image_url?: string;
      map_zoom?: number;
      map_size?: string;
    }) => {
      return apiMutation<SolarBuildingInsightsResult>(
        '/api/property/solar-building-insights',
        input
      );
    },
  });
}

export function useDsm3DPreview() {
  return useMutation({
    mutationFn: async (input: {
      dsm_url: string;
      rgb_url?: string;
      mask_url?: string;
      title_geojson?: unknown;
      panels?: unknown[];
      include_panels?: boolean;
      z_scale?: number;
      output_formats?: string[];
      buffer_meters?: number;
      refresh?: boolean;
    }) => {
      return apiMutation<{
        elevation_preview_path?: string;
        elevation_preview_path_url?: string;
        mesh_glb_path?: string;
        mesh_glb_path_url?: string;
        mesh_obj_path?: string;
        mesh_obj_path_url?: string;
        hillshade_path?: string;
        hillshade_path_url?: string;
        stats: {
          z_min: number;
          z_max: number;
          width: number;
          height: number;
          vertices?: number;
          faces?: number;
          clipped: boolean;
          crs: string;
          bounds: [number, number, number, number];
          ground_frame?: {
            origin_longitude: number;
            origin_latitude: number;
            east_longitude_per_m: number;
            east_latitude_per_m: number;
            south_longitude_per_m: number;
            south_latitude_per_m: number;
          } | null;
        };
      }>('/api/property/dsm-3d-preview', input);
    },
  });
}

export function useSubmitHelioQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: HelioQuoteSubmitInput) =>
      apiMutation<Project>('/api/quotes/submit-helio', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['portalUser'] });
    },
  });
}

export interface FunnelEnergyPlanPreviewInput {
  emailConsent: boolean;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  billData: BillData & { postcode?: string; monthly_bill?: number };
  includesEv: boolean;
  includesHeatPump: boolean;
  estimate?: {
    annualBenefit: number;
    monthlySaving: number;
    panels: number;
    kwp: number;
    batteryKwh: number;
    currentMonthlyBill?: number | null;
  };
}

export function useSendFunnelEnergyPlanPreview() {
  return useMutation({
    mutationFn: (input: FunnelEnergyPlanPreviewInput) =>
      apiMutation<{ quoteId: number; proposalReference: string }>(
        '/api/quotes/funnel-preview-email',
        input,
      ),
  });
}

export function useProposalHtml(quoteId?: number) {
  return useQuery({
    queryKey: ['proposalHtml', quoteId],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/quotes/${quoteId}/proposal-html`, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) {
        throw new Error('Failed to load proposal preview');
      }
      return response.text();
    },
    enabled: !!quoteId && quoteId > 0,
    staleTime: 60_000,
  });
}

export function useMyProjects(
  enabled = true,
  scope?: 'customer' | 'installer',
  refetchInterval?: number | false,
) {
  const params = scope ? `?scope=${scope}` : '';
  return useQuery({
    queryKey: ['projects', scope ?? 'default'],
    queryFn: () => apiRequest<Project[]>(`/api/projects/mine${params}`),
    enabled,
    refetchInterval: refetchInterval === undefined ? false : refetchInterval,
  });
}

export function useAvailableLeads(enabled = true) {
  return useQuery({
    queryKey: ['availableLeads'],
    queryFn: () => apiRequest<Project[]>('/api/leads/mine'),
    enabled,
  });
}

export function useAcceptLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: number) => apiMutation<Project>(`/api/leads/${leadId}/accept`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['availableLeads'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeclineLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: number) => apiMutation(`/api/leads/${leadId}/reject`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['availableLeads'] });
    },
  });
}

export function useInstallerAvailability(enabled = true) {
  return useQuery({
    queryKey: ['installerAvailability'],
    queryFn: () => apiRequest<InstallerAvailability>('/api/installer/availability'),
    enabled,
  });
}

export function useUpdateInstallerAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dates: string[]) =>
      apiMutation<InstallerAvailability>('/api/installer/availability', { availableOn: dates }, 'PUT'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['installerAvailability'] });
      void queryClient.invalidateQueries({ queryKey: ['availableLeads'] });
    },
  });
}

export function useQuoteInstallDates(quoteId?: number | null, enabled = true) {
  return useQuery({
    queryKey: ['quoteInstallDates', quoteId],
    queryFn: () => apiRequest<QuoteInstallDates>(`/api/quotes/${quoteId}/available-install-dates`),
    enabled: enabled && !!quoteId && quoteId > 0,
  });
}

export function useSetQuoteAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteId, date }: { quoteId: number; date: string }) =>
      apiMutation<Project>(`/api/quotes/${quoteId}/availability`, { customerAvailableDate: date }, 'PUT'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['quoteInstallDates'] });
    },
  });
}

export function useProjectMessages(projectId: number, enabled = true) {
  return useQuery({
    queryKey: ['messages', projectId],
    queryFn: () => apiRequest<ProjectMessage[]>(`/api/projects/${projectId}/messages`),
    enabled: enabled && projectId > 0,
  });
}

export function useSendMessage(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiMutation<ProjectMessage>(`/api/projects/${projectId}/messages`, { content }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['messages', projectId] }),
  });
}

export function useProjectDocuments(projectId: number, enabled = true) {
  return useQuery({
    queryKey: ['documents', projectId],
    queryFn: () =>
      apiRequest<Array<{ id: number; name: string; category: string; signedUrlEndpoint?: string; fileUrl?: string; createdAt?: string }>>(
        `/api/projects/${projectId}/documents`,
      ),
    enabled: enabled && projectId > 0,
  });
}

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiRequest<UserNotification[]>('/api/notifications'),
    enabled,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiMutation(`/api/notifications/${id}/read`, undefined, 'PATCH'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiMutation('/api/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMyReferrals(enabled = true) {
  return useQuery({
    queryKey: ['referrals'],
    queryFn: () => apiRequest<Referral[]>('/api/referrals/mine'),
    enabled,
  });
}

export function useCreateReferral() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiMutation<{ referralCode: string }>('/api/referrals'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['referrals'] }),
  });
}

export function useApproveQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: number) =>
      apiMutation(`/api/projects/${projectId}/approve`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useUpdateProjectStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: number;
      stage: string;
      surveyDate?: string;
      installDate?: string;
      surveyNotes?: string;
    }) =>
      apiMutation(`/api/projects/${input.id}/stage`, input, 'PATCH'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useInstallerProfile(enabled = true) {
  return useQuery({
    queryKey: ['installerProfile'],
    queryFn: () => apiRequest<InstallerProfile>('/api/installer/profile'),
    enabled,
  });
}

export function useUpdateInstallerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<InstallerProfile>) =>
      apiMutation<InstallerProfile>('/api/installer/profile', input, 'PUT'),
    onSuccess: (data) => {
      queryClient.setQueryData(['installerProfile'], data);
      queryClient.invalidateQueries({ queryKey: ['installerProfile'] });
    },
  });
}

export function useInstallerApply() {
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      apiMutation('/api/installer/applications', input),
  });
}

export function useDepositCheckout() {
  return useMutation({
    mutationFn: ({ projectId, origin }: { projectId: number; origin: string }) =>
      apiMutation<{ url: string }>('/api/payments/deposit-checkout', { projectId, origin }),
  });
}

export function useContractReady() {
  return useMutation({
    mutationFn: (quoteId: number) =>
      apiMutation<{ success: boolean; signing_url?: string; message?: string; status?: string }>(
        `/api/quotes/${quoteId}/contract-ready`,
      ),
  });
}

export function useContractSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quoteId: number) =>
      apiMutation<{
        success: boolean;
        lifecycleStatus?: string;
        contractSignedAt?: string | null;
      }>(`/api/quotes/${quoteId}/contract-sync`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export interface FinanceConfig {
  applyUrl: string;
  provider: string;
  disclosureText: string;
  disclosureEnabled?: boolean;
  phoenixDisclosureText?: string;
  iarFrn?: string;
  bannerUrl?: string;
}

export function useFinanceConfig() {
  return useQuery({
    queryKey: ['financeConfig'],
    queryFn: () => apiRequest<FinanceConfig>('/api/config/finance'),
    staleTime: 60 * 60_000,
  });
}

/** Active system options for the homepage example. Public endpoint; no auth. */
export function useSystemOptions() {
  return useQuery({
    queryKey: ['systemOptions'],
    queryFn: () => apiRequest<unknown>('/api/system-options'),
    staleTime: 60 * 60_000,
    retry: false,
  });
}

export function useFinanceUrlClick() {
  return useMutation({
    mutationFn: (quoteId: number) =>
      apiMutation<{
        success: boolean;
        finance_url_clicked_at?: string;
        finance_outcome?: string;
        applyUrl?: string;
      }>(`/api/quotes/${quoteId}/finance-url-click`),
  });
}

export function useBlogPosts(params: { page?: number; tag?: string } = {}) {
  return useQuery({
    queryKey: ['blogPosts', params.page ?? 1, params.tag ?? ''],
    queryFn: () => fetchBlogPosts(params),
    staleTime: 60_000,
  });
}

export function useBlogPost(slug: string | undefined) {
  return useQuery({
    queryKey: ['blogPost', slug ?? ''],
    queryFn: () => fetchBlogPost(slug as string),
    enabled: Boolean(slug),
    staleTime: 60_000,
    retry: false,
  });
}

export function useRemoteSurvey(projectId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['remoteSurvey', 'project', projectId ?? 0],
    queryFn: () => apiRequest<RemoteSurveyPayload>(`/api/projects/${projectId}/remote-survey`),
    enabled: enabled && !!projectId && projectId > 0,
  });
}

export function useInstallerSurvey(surveyId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['remoteSurvey', 'installer', surveyId ?? 0],
    queryFn: () => apiRequest<RemoteSurveyPayload>(`/api/installer/surveys/${surveyId}`),
    enabled: enabled && !!surveyId && surveyId > 0,
  });
}

export function useInstallerSurveyList(enabled = true) {
  return useQuery({
    queryKey: ['installerSurveys'],
    queryFn: () => apiRequest<InstallerSurveyListItem[]>('/api/installer/surveys'),
    enabled,
  });
}

export function useCreateInstallerSurvey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiMutation<RemoteSurveyPayload>('/api/installer/surveys'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installerSurveys'] });
    },
  });
}

export function useAttachSurvey(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (surveyId: number) =>
      apiMutation<RemoteSurveyPayload>(`/api/projects/${projectId}/attach-survey/${surveyId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installerSurveys'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['remoteSurvey'] });
      queryClient.invalidateQueries({ queryKey: ['attachSuggestions', projectId] });
    },
  });
}

export function useAttachSuggestions(projectId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['attachSuggestions', projectId ?? 0],
    queryFn: () => apiRequest<InstallerSurveyListItem[]>(`/api/projects/${projectId}/attach-suggestions`),
    enabled: enabled && !!projectId && projectId > 0,
  });
}

function surveyCacheKey(projectId?: number, surveyId?: number) {
  return surveyId ? (['remoteSurvey', 'installer', surveyId] as const) : (['remoteSurvey', 'project', projectId ?? 0] as const);
}

function surveyBasePath(projectId?: number, surveyId?: number) {
  return surveyId ? `/api/installer/surveys/${surveyId}` : `/api/projects/${projectId}/remote-survey`;
}

export function useSaveRemoteSurvey(projectId: number, surveyId?: number) {
  const queryClient = useQueryClient();
  const key = surveyCacheKey(projectId, surveyId);
  return useMutation({
    mutationFn: (body: {
      sections?: Record<string, Record<string, unknown>>;
      current_section?: string;
      customer_notes?: string | null;
      idle?: boolean;
    }) => apiMutation<RemoteSurveyPayload>(surveyBasePath(projectId, surveyId), body, 'PATCH'),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
    },
  });
}

export function useUploadSurveyPhoto(projectId: number, surveyId?: number) {
  const queryClient = useQueryClient();
  const key = surveyCacheKey(projectId, surveyId);
  return useMutation({
    mutationFn: async (input: { file: File; photoType: string; capturePath: string }) => {
      const form = new FormData();
      form.append('file', input.file);
      form.append('photo_type', input.photoType);
      form.append('capture_path', input.capturePath);
      return apiMutation<{ image: { id: number; photoType: string; status: string }; survey: RemoteSurveyPayload }>(
        `${surveyBasePath(projectId, surveyId)}/photos`,
        form,
      );
    },
    onSuccess: (data) => {
      queryClient.setQueryData(key, data.survey);
    },
  });
}

export function useUploadInstallerSurveyBill(surveyId: number | undefined) {
  const queryClient = useQueryClient();
  const key = surveyCacheKey(0, surveyId);
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return apiMutation<RemoteSurveyPayload>(`/api/installer/surveys/${surveyId}/utility-bill`, form);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
    },
  });
}

export function useDeleteSurveyPhoto(projectId: number, surveyId?: number) {
  const queryClient = useQueryClient();
  const key = surveyCacheKey(projectId, surveyId);
  return useMutation({
    mutationFn: (imageId: number) =>
      apiMutation(`${surveyBasePath(projectId, surveyId)}/photos/${imageId}`, undefined, 'DELETE'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

export function useSubmitRemoteSurvey(projectId: number, surveyId?: number) {
  const queryClient = useQueryClient();
  const key = surveyCacheKey(projectId, surveyId);
  return useMutation({
    mutationFn: () => apiMutation<RemoteSurveyPayload>(`${surveyBasePath(projectId, surveyId)}/submit`),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['installerSurveys'] });
    },
  });
}

export function useInstallerPack(projectId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['installerPack', projectId ?? 0],
    queryFn: () => apiRequest<InstallerPackPayload>(`/api/projects/${projectId}/installer-pack`),
    enabled: enabled && !!projectId && projectId > 0,
  });
}

export function useSaveInstallerNote(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note: string) =>
      apiMutation(`/api/projects/${projectId}/remote-survey/installer-note`, { note }, 'PATCH'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installerPack', projectId] });
      queryClient.invalidateQueries({ queryKey: ['remoteSurvey', projectId] });
    },
  });
}

